import { fetchLastCustomerMessage, clearStuckTickets } from '../api/messages';
import { escalateConversation, autoEscalateConversation, getConversation, getConversationById } from '../api/tickets';
import { getGlobalParseConfigsList } from '../api/platformConfig';
import * as types from './types';
import {
  trackTakeTicket,
  trackEscalatedTicket,
  trackErrorFetchingSmbCategories,
} from '../analytics/ticketEventAnalytics';
import { trackSameResponseUsed, trackBlacklistTextEvent } from '../analytics/messageEventAnalytics';
import { trackSendButtonClick } from '../analytics/buttonEventAnalytics';
import { commerceRecosDisplayed } from '../analytics/mlEventAnalytics';
import {
  trackDecisionTreeTextCopied,
  SEARCH_ACTION_SOURCES,
  trackDecisionTreeCaseNotHandled,
} from '../analytics/searchEventAnalytics';
import cloneDeep from 'lodash/cloneDeep';
import isFunction from 'lodash/isFunction';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { chooseDefaultTicket, formatEscalateEmail, htmlToText, messageToClientFormat } from '../util';
import { CloudFunctionRequestError } from '@asurion-core/frontend-utils/lib/ErrorsModule/ErrorsModule';
import { addPrefixToEsclationMessage } from '../util/formatUtil';
import { DraftHelpers } from '../util/Draft';
import { processAssignedTicket, processMessage } from '../util/dataObjectToStoreObject';
import { showErrorPopup } from '../components/popups/ErrorPopup/ErrorPopup';
import { clearTools, loadECommerceIntegrations, loadDynamicIntegrations } from './tools/ToolActions';
import { showErrorMessage, showExpressError, showError } from '../util/errorPopupUtil';
import { HINT, SPECIALIST_ONLY_HINT } from '../constants/noteTypes';
import { NOMINATED, ASSIGNED, ALL, LIVE_CHAT } from '../constants/tabNames';
import { CANNOT_HANDLE_POPUP_CONFIG, oneBtnPopupCreator } from '../components/popups/ConfirmPopup/helper';
import {
  canEscalateCurrentTicket,
  extractNominatedTickets,
  extractSelectedInboxTab,
  extractSelectedTicketBusinessDetailsFromState,
  extractSelectedTicketFromState,
  extractSelectedTicketIdFromState,
  extractTicketFromStateById,
  getDecisionNodeAtPath,
  getLastSuccessfulMessageFromSimplr,
  hasExpertReplied,
  isSpecialist,
  getSelectedQueue,
  getNumberOfUnreadBusinessAlerts,
  getQueueNamesForTicket,
  getOutboundEscalationObject,
  getNewMessagesEditor,
  getPrevSelectedTicketId,
  isSelectedTicketReviewTicket,
  isTicketAlreadyInQueue,
  extractSelectedHistoricalTicketIdFromState,
  extractDisplayingHistoricalConversationFromState,
  getSimplrAccountIdOfSelectedTicket,
  extractCommerceWizardCrossSellProducts,
  extractCommerceWizardUpsSellProducts,
  extractCommerceWizardGlobalProduct,
  extractPreviouslyRecommendedProducts,
  extractDecisionTreeOfSelectedTicket,
  extractFirstNominatedNotification,
  extractNominatedNotificationActive,
  getChatAlertActiveId,
  extractCommerceWizardRecommendationData,
  extractNonHistoricalTicketIdFromState,
  getSelectedDiscountPeriodCouponCode,
  getShowToolsState,
  getBrandOfSelectedTicket,
  getFlowDebugModeStatus,
  extractFirstMessage,
  getHasSearchedAllTab,
} from '../extractFromState';
import { createMessage } from './../util/objectFactories';
import * as triggers from 'constants/commerceTriggers';
import { isEscalatableProvider } from '../util/ticketSelector';
import { addTicket, updateTicket, hydrateHistoricalTickets } from './ticketActions';
import { clearCommerceWizardState } from 'actions/CommerceWizardActions';
import { loadTicketsAdmin, loadTicketsNonAdmin, getMlOrders } from './creatorsAsync';
import { getZendeskErrorDescription } from '../util/error';
import { clearMacroWizardPath, clearBusinessAlerts } from './searchActions';
import { getShowTools } from '../selectors/tools';
import { DECISION_NODE_ACTIONS } from '../constants/decisionTree';
import { getAssignedTickets } from '../api/tickets';
import { filterEscalatedTickets } from './escalatedTabFiltersActions';
import {
  lockConversation,
  unlockConversation,
  setRecommendationAsPreviouslyTriggered,
  hydrateAssignedTickets,
} from './creatorsSync';
import { handleError, logMessage } from 'services/ErrorService/ErrorService';
import { ErrorStrings } from '../constants/fixedMessages';
import { loadBusinessDetailsItem } from 'actions/businessActions';
import * as infoPanelTypes from 'constants/infoPanelTypes';
import { newNominationNotifications, removeNotification } from 'actions/notificationActions';
import {
  setInProgressTreeAndWizardLocation,
  clearInProgressTreeAndWizardLocation,
} from 'actions/inProgressTreeAndWizardLocationActions';
import { TICKET_STATUS_FILTER } from 'constants/ticketStatuses';
import { ALL as ALL_TAB } from 'constants/tabNames';
import makeCloudFunctionRequest from '../api/makeCloudFunctionRequest';
import { isLiveChat } from 'util/chat';
import { getTemplatedCopyTextFromCurrentDecisionNode } from '../selectors/templating';
import { getPartnerTags } from './tools/TaggingActions';
import { setConversationBlockedWordsFound, setConversationOverridableWordsFound } from 'actions/conversationActions';
import { getExpertId } from 'util/parseSelectors';
import { SELECT_TICKET_LOADING } from 'constants/loading';
import AuthService from 'services/authService';
import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';

export const selectInboxTab = (tabName) => {
  return async (dispatch, getState) => {
    const state = getState();

    // persist tree location
    dispatch(setInProgressTreeAndWizardLocation());

    const selectedInboxTab = extractSelectedInboxTab(state);
    if (selectedInboxTab === tabName) {
      return; // Do nothing
    }

    const isAdmin = isSpecialist(state);

    // If current user is admin - handle filters
    if (isAdmin) {
      // NOTE(vlad|SPD-14305|2026-02-27): All tab does NOT auto-load tickets.
      // Queue.jsx checks hasSearchedAllTab to gate display — no clearing needed here.
      // Searched results persist across tab switches.

      // Escalated and My Items always load fresh data on switch.
      if (tabName.toLowerCase() === NOMINATED) {
        await dispatch(filterEscalatedTickets(true));
      }
      if (tabName.toLowerCase() === ASSIGNED) {
        const assignedTickets = await getAssignedTickets(true);
        dispatch(hydrateAssignedTickets(assignedTickets));
      }
    }

    dispatch(selectInboxTabWithLoad(tabName));
  };
};

// used to switch the selected ticket to a default one in the tab after review submission by PP
// if the queue doesn't have any tickets it clears the selected ticket id
export const selectDefaultTicket = () => (dispatch, getState) => {
  const state = getState();
  const selectedTabName = extractSelectedInboxTab(state);

  let defaultTicketId;

  try {
    defaultTicketId = chooseDefaultTicket(selectedTabName, state);
  } catch (error) {
    handleError({ error });
  }

  if (defaultTicketId) {
    dispatch(selectTicket(defaultTicketId, selectedTabName));
  } else {
    dispatch(clearSelectedTicket());
  }
};

export const selectInboxTabWithLoad = (tabName) => (dispatch, getState) => {
  /*
   *  Before dispatching SELECT_INBOX_TAB:
   *      1) selectTicket() with the first item in the queue if state.selected{Queue}TicketId is empty in the redux store.
   *      2) loadConverstaion() if the queue is not empty
   */
  const state = getState();
  const currSelectedTicketId = state.selectedTicketId;
  const currSelectedTicketExistsInTheDestinationTab = isTicketAlreadyInQueue(state, tabName, currSelectedTicketId);
  // prevSelectedTicketIdInTab is the ticket id that was selected the last time the tab was opened
  const prevSelectedTicketIdInTab = getPrevSelectedTicketId(state, tabName);

  let ticketId;

  // NOTE(vlad|SPD-14335|2026-03-02): For ALL tab, don't auto-select a ticket if search hasn't been performed.
  // This prevents showing a stale conversation in the right panel when the queue shows the empty search state.
  const hasSearched = tabName === ALL_TAB ? getHasSearchedAllTab(state) : true;

  if (!hasSearched) {
    ticketId = '';
  } else if (currSelectedTicketExistsInTheDestinationTab) {
    // if the currently selected ticket exists in the destination tab, keep the selectedTicketId
    ticketId = state.selectedTicketId;
  } else if (prevSelectedTicketIdInTab) {
    // if the currently selected ticket doesn't exist, set selectedTicketId to previously selected in the tab
    ticketId = prevSelectedTicketIdInTab;
  } else {
    // if non of the above, select a default ticket in the tab
    ticketId = chooseDefaultTicket(tabName, state) || '';
  }

  // current selectedTicketId at the moment of tab switching will be saved in previousSelectedTicketId
  // if selected ticket is a review ticket, don't save
  const currNonReviewSelectedTicketId = !isSelectedTicketReviewTicket(state) ? currSelectedTicketId : '';
  const currSelectedTab = extractSelectedInboxTab(state);

  // clears selectedTicket AND historicalTicket before switching tabs to avoid invalid selectedTicketId
  // NOTE(vlad|SPD-14335|2026-03-02): Also clear when ALL tab hasn't been searched yet,
  // even if the ticket exists in the stale allTickets store from initial load.
  if (!isSelectedTicketReviewTicket(state) && (!currSelectedTicketExistsInTheDestinationTab || !hasSearched)) {
    dispatch(clearSelectedTicket());
    dispatch(clearHistoricalTicket());
  }

  dispatch({
    type: types.SELECT_INBOX_TAB,
    tabName,
    prevSelectedTicketId: currNonReviewSelectedTicketId,
    prevSelectedTab: currSelectedTab,
  });

  // NOTE(vlad|SPD-14335|2026-03-02): Skip selectTicket when ALL tab hasn't been searched —
  // selectTicket('') would fall back to chooseDefaultTicket and re-select a stale ticket.
  if (hasSearched) {
    dispatch(selectTicket(ticketId, tabName));
  }
};

export const updateTicketMlData = (conversationId, mlData) => {
  return async () => {
    try {
      await makeCloudFunctionRequest('updateConversationMlData', { conversationId, mlData });
    } catch (err) {
      showError(err);
    }
  };
};

// ticket/conversation must .include customerPtr
export const addTicketToQueue = ({ conversation, queueName, nominationExpiresAt, showNotification }) => {
  return async (dispatch, getState) => {
    // prevents alert noise when adding ticket to queue
    dispatch(toggleChatAlertOnAndOff());

    const state = getState();
    const isAdmin = isSpecialist(state);

    if (queueName === NOMINATED) {
      try {
        const lastCustomerMessage = await fetchLastCustomerMessage(conversation);
        const lastCustomerMessageBody = lastCustomerMessage?.messageBody;

        dispatch(addTicket(conversation, queueName, isAdmin, lastCustomerMessageBody, nominationExpiresAt));

        if (showNotification && !isAdmin) {
          dispatch(newNominationNotifications(conversation.objectId));
        }

        const selectedInboxTab = extractSelectedInboxTab(state);
        if (selectedInboxTab === NOMINATED) {
          const nominatedTickets = extractNominatedTickets(state);
          const isReviewTicketSelected = isSelectedTicketReviewTicket(state);
          if (nominatedTickets.allTickets.length === 1 && !isReviewTicketSelected) {
            await dispatch(selectTicket(nominatedTickets.allTickets[0], NOMINATED));
          }
        }
      } catch (err) {
        showError(err);
      }
    } else {
      dispatch(addTicket(conversation, queueName, isAdmin));
    }
  };
};

export const removeTicketFromQueue = (ticketId, queueName, isAdmin) => {
  return {
    type: types.REMOVE_TICKET,
    ticketId: ticketId,
    queueName: queueName,
    isAdmin: isAdmin,
  };
};

/**
 * selects ticket and loads associated messages conversation messages
 * @param {string} ticketId - ID of ticket being selected
 * @param {string} messageCategory - category of ticket being selected
 * @param {boolean} isHistoricalTicket - is the ticket a historical ticket
 * @param {boolean} isExternalHistoricalTicket - is the historical ticket stored in external system
 * @param {boolean} resetPanels - skip for reset wizard and panels
 */
export const selectTicket =
  (ticketId, messageCategory, isHistoricalTicket, isExternalHistoricalTicket, resetPanels = true) =>
  async (dispatch, getState) => {
    const state = getState();
    // choose default id if none is passed
    const selectedTicketId = ticketId || chooseDefaultTicket(messageCategory, state);
    const selectedHistoricalTicketId = extractSelectedHistoricalTicketIdFromState(state);
    const currentSelectedTicketId = extractSelectedTicketIdFromState(state);
    const isAdmin = isSpecialist(state);
    const selectedInboxTab = extractSelectedInboxTab(state);
    const actionTabActive = getShowTools(state);
    const ticketAlreadySelected = selectedTicketId === currentSelectedTicketId && !selectedHistoricalTicketId;
    const historicalTicketAlreadySelected =
      selectedTicketId === selectedHistoricalTicketId && selectedHistoricalTicketId;
    const displayingHistoricalConversation = extractDisplayingHistoricalConversationFromState(state);
    const toolsCurrentlyOpen = getShowToolsState(state);
    const session = uuidv4(); // used to track ticket sessions in analytics events

    if (!selectedTicketId) {
      // no id, nothing to select
      return;
    }

    if (!displayingHistoricalConversation && (ticketAlreadySelected || historicalTicketAlreadySelected)) {
      // prevents unneeded API calls
      return;
    }

    // Although not a component, we set whether the action is loading or not
    // to allow components that need to listen on these state updates to complete.
    dispatch(setLoadingUnique(true, SELECT_TICKET_LOADING));

    // clears incase ticket is taken mid tag
    dispatch(setTaggingActive(false));
    dispatch(clearHistoricalTicket());

    if (resetPanels) {
      // 1. Persist current wizard state - we want to persist parent tools
      await dispatch(setInProgressTreeAndWizardLocation(currentSelectedTicketId));

      // 2. Clear data from previous conversation
      dispatch(clearTools());
      dispatch(closePreviousConversationsSection());

      // 3. Close tools - forces users to reopen the tab and reload macros
      if (toolsCurrentlyOpen) {
        dispatch(setShowTools(false));
      }
    }

    dispatch({
      type: 'SELECT_' + messageCategory.toUpperCase() + '_TICKET',
      selectedTicketId,
      isAdmin,
      session,
    });

    if (isHistoricalTicket) {
      await dispatch(loadHistoricalConversation(selectedTicketId, selectedInboxTab, isExternalHistoricalTicket));
    } else {
      await dispatch(loadConversation(selectedTicketId, selectedInboxTab));
    }

    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(getState());
    const postSelectActions = [dispatch(loadBusinessDetailsItem(simplrAccountId))];

    if (actionTabActive && !isHistoricalTicket) {
      postSelectActions.push(dispatch(loadActionsInfo(selectedTicketId)));
    }

    await Promise.all(postSelectActions);
    dispatch(setLoadingUnique(false, SELECT_TICKET_LOADING));
  };

/**
 * loads the messages in a conversation
 * @param {string} conversationObjectId - ID of conversation being loaded
 * @param {string} tabName - tab of the ticket
 */
export const loadConversation = (conversationObjectId, tabName) => {
  return async (dispatch, getState) => {
    // TODO: Please test and remove this if condition if its not needed anymore
    dispatch(setLoading(true, 'messages'));
    dispatch(setTagOnForwardStatus(false));
    dispatch(clearBusinessAlerts());
    if (!conversationObjectId) {
      dispatch({
        type: types.LOAD_CONVERSATION,
        conversation: { allMessages: [], selectedConversationId: '' },
      });
      dispatch(setLoading(false, 'messages'));
    } else {
      let loadedConversationObjId = getState().conversation.selectedConversationId;

      // Before making the api.getConversation() request checking if the requested conversation is already loaded
      if (conversationObjectId !== loadedConversationObjId) {
        let conversation,
          selectedTicketId,
          currentTab,
          userIsStillOnSameTab,
          conversationNotYetLoaded,
          loadedConversationIsSelected;
        try {
          const ticket = extractSelectedTicketFromState(getState());
          const simplrAccountId = get(ticket, 'simplrAccountPtrId');
          conversation = await getConversation(
            conversationObjectId,
            getFlowDebugModeStatus(getState()),
            simplrAccountId
          );
          loadedConversationObjId = getState().conversation.selectedConversationId; // get fresh copy
          selectedTicketId = extractSelectedTicketIdFromState(getState());
          currentTab = extractSelectedInboxTab(getState());
          conversationNotYetLoaded = loadedConversationObjId !== conversationObjectId;
          userIsStillOnSameTab = tabName === currentTab;
          loadedConversationIsSelected = selectedTicketId === conversationObjectId;

          if (userIsStillOnSameTab && conversationNotYetLoaded && loadedConversationIsSelected) {
            dispatch({
              type: types.LOAD_CONVERSATION,
              conversation,
            });
            dispatch(getMlOrders());
          }

          if (loadedConversationIsSelected) {
            dispatch(setLoading(false, 'messages'));
          }
        } catch (error) {
          // do not setLoading = false - we need to hide the conversation if this call fails
          handleError({
            error,
            customMessage: 'Error getting conversations',
            showPopup: true,
            customFields: {
              conversation,
              selectedTicketId,
              currentTab,
              userIsStillOnSameTab,
              conversationNotYetLoaded,
              loadedConversationIsSelected,
            },
          });
        }
      } else {
        dispatch(setLoading(false, 'messages'));
      }
    }
  };
};

export const closePreviousConversationsSection = () => ({
  type: types.CLOSE_PREVIOUS_CONVERSATIONS_SECTION,
});

export const openPreviousConversationsSection = () => ({
  type: types.OPEN_PREVIOUS_CONVERSATIONS_SECTION,
});

export const closeConversationAttachmentsSection = () => ({
  type: types.CLOSE_ATTACHMENTS_SECTION,
});

export const openConversationAttachmentsSection = () => ({
  type: types.OPEN_ATTACHMENTS_SECTION,
});

// if ticket is in state under a particular category
const ticketExists = (ticketId, messageCategory, state) =>
  (messageCategory === ALL && state.tickets.allTickets.hasOwnProperty(ticketId)) ||
  (messageCategory === NOMINATED && state.tickets.nominatedTickets.hasOwnProperty(ticketId)) ||
  (messageCategory === ASSIGNED && state.tickets.assignedTickets.hasOwnProperty(ticketId));

/**
 * Highlights ticket on left panel and loads conversation if ticket is present in state for a given message category
 * @param ticketId {String}
 * @param messageCategory {String}
 */
export const loadTicketIfExists = (ticketId, messageCategory) => (dispatch, getState) => {
  if (ticketExists(ticketId, messageCategory, getState())) {
    dispatch(selectTicket(ticketId, messageCategory));
  }
};

export const moveTicketFromNominatedToAssigned = (ticket) => {
  return {
    type: types.MOVE_TICKET,
    conversationObjectId: ticket.ticketId,
    communicationType: ticket.communicationType,
    ticket,
  };
};

export const moveTicketFromAllToAssigned = (ticket) => {
  return {
    type: types.MOVE_TICKET_FROM_ALL_TO_ASSIGNED,
    conversationObjectId: ticket.ticketId,
    communicationType: ticket.communicationType,
    ticket,
  };
};

export const assignConversationToExpertAndSelect = (conversationId, simplrAccountPtrId, buttonId, source = '') => {
  // eslint-disable-next-line
  return async function assignConversationToExpertAndSelect(dispatch, getState) {
    try {
      let conversationObj;
      dispatch(removeNotification(conversationId));

      try {
        conversationObj = await dispatch(
          assignConversationToExpert(conversationId, simplrAccountPtrId, buttonId, source)
        );
      } catch (error) {
        // DataIntegrityError is thrown from the server if ticket is no longer nominated to the expert
        if (error?.message?.name === 'DataIntegrityError') {
          return handleError({
            customMessage: 'The ticket cannot be taken because the nomination expired',
            showPopup: true,
          });
        }

        return handleError({ error, showPopup: true });
      }

      const previouslySelectedTicketId = extractSelectedTicketIdFromState(getState());
      const showToolsBefore = getShowTools(getState());

      if (conversationId !== previouslySelectedTicketId) {
        dispatch(clearTools());
      }

      await dispatch(moveTicketToAssignedForExpert(conversationObj));

      // NOTE(vlad|SPD-15261|2026-07-28): Taking a ticket opens the Actions panel (MOVE_TICKET sets
      // showTools=true for non-chat). Load action data only when Take actually opens the panel
      // (showTools false->true) — including when the ticket was previewed first, which is the bug:
      // otherwise selectActionTab/setSearchType never runs and SearchPage sticks on its Loading
      // fallback so the expert cannot select tags. Guarding on the transition (not just getShowTools)
      // avoids a redundant second load in the tag-picker flow, where setShowTools(true) already loaded
      // actions when the picker opened (this is the double-load SPD-3520's guard originally prevented).
      if (!showToolsBefore && getShowTools(getState())) {
        await dispatch(loadActionsInfo(conversationId));
      }
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    }
  };
};

// Dispatched when a chat is taken using <NominatedChatPopupContainer>
export const assignConversationToExpertWithoutSelect = (ticket, source = '') => {
  return async (dispatch, getState) => {
    try {
      dispatch(removeNotification(ticket.ticketId));

      const conversationObj = await dispatch(
        assignConversationToExpert(ticket.ticketId, ticket.simplrAccountPtrId, undefined, source)
      );

      dispatch(moveTicketToAssignedForExpertWithoutSelect(conversationObj));

      /**
       * If there's no selected ticket, then the queue for the selected inbox tab was empty prior to this
       * ticket being taken.
       *
       * Typically, we do not want to select a ticket that has been taken using <NominatedChatPopupContainer>.
       * But if this is the only ticket in the queue, then it will be displayed in the right panel automatically.
       * To ensure that the right panel works correctly, we must select this ticket.
       */
      if (!getState().selectedTicketId) {
        dispatch(selectDefaultTicket());
      }
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    }
  };
};

// does not move ticket to new queue
const assignConversationToExpert = (conversationId, simplrAccountPtrId, buttonId, source = '') => {
  // eslint-disable-next-line
  return async function assignConversationToExpert(dispatch, getState) {
    const simplrExpertObjectId = AuthService.current().simplrExpert.id;
    const { platformDomain, platformTicketId } = extractTicketFromStateById(getState(), conversationId);

    const params = {
      simplrExpertObjectId,
      conversationObjectId: conversationId,
      platformDomain,
      platformTicketId,
    };

    trackTakeTicket(source);

    let conversationObj;
    try {
      dispatch(lockConversation(conversationId));
      conversationObj = await makeCloudFunctionRequest('assignConversationToExpert', params);
    } catch (error) {
      // DataIntegrityError is thrown from the server if ticket is no longer nominated to the expert
      if (error.response?.code === SimplrErrors.Codes.DATA_INTEGRITY_ERROR) {
        dispatch(removeTicketFromQueue(conversationId, NOMINATED, isSpecialist(getState())));
      }

      throw error;
    } finally {
      dispatch(unlockConversation(conversationId));

      // remove ticket from nominated tab if it's still there
      const state = getState();
      const nominatedTicketsIds = state.tickets.nominatedTickets.allTickets || [];

      if (nominatedTicketsIds.includes(conversationId)) {
        dispatch(removeTicketFromQueue(conversationId, NOMINATED, false));
      }
    }
    // reset button active state - disabled on click to prevent double clicks
    if (buttonId) {
      await dispatch(setBtnActivity(buttonId, true));
    }

    return conversationObj;
  };
};
export const moveTicketToAssignedForExpertWithoutSelect = (conversationObj) => {
  return (dispatch) => {
    const ticket = processAssignedTicket(conversationObj);
    dispatch(toggleChatAlertOnAndOff());
    dispatch(moveTicketFromNominatedToAssigned(ticket));
  };
};

export const moveTicketToAssignedForExpert = (conversationObj) => {
  return async (dispatch) => {
    dispatch(moveTicketToAssignedForExpertWithoutSelect(conversationObj));

    await dispatch(
      selectTicket(conversationObj.objectId, isLiveChat(conversationObj.communicationType) ? LIVE_CHAT : ASSIGNED)
    );
    /*
     * selectInboxTab is a thunk which dispatches selectInboxTabWithLoad
     * selectInboxWithLoad dispatches actions.selectTicket() & actions.loadConversation() if it's required.
     */
    if (isLiveChat(conversationObj.communicationType)) {
      dispatch(selectInboxTab(LIVE_CHAT));
    } else {
      dispatch(selectInboxTab(ASSIGNED));
    }
  };
};

export const moveTicketToAssignedForSpecialist = (conversationObj, simplrExpertObjectId, tabName) => {
  return async (dispatch) => {
    const ticket = processAssignedTicket(conversationObj);
    if (tabName === NOMINATED) {
      dispatch(moveTicketFromNominatedToAssigned(ticket));
    } else if (tabName === ALL) {
      dispatch(moveTicketFromAllToAssigned(ticket, simplrExpertObjectId));
    }
    await dispatch(selectTicket(conversationObj.objectId, ASSIGNED));
    /*
     * selectInboxTab is a thunk which dispatches selectInboxTabWithLoad
     * selectInboxWithLoad dispatches actions.selectTicket() & actions.loadConversation() if it's required.
     */
    dispatch(selectInboxTab(ASSIGNED));
  };
};

export const assignConversationToSpecialist = (conversationId, tabName, buttonId, source = '') => {
  // eslint-disable-next-line
  return async function assignConversationToSpecialist(dispatch) {
    const simplrExpertObjectId = AuthService.current().simplrExpert.id;
    let params = {
      simplrExpertObjectId,
      conversationObjectId: conversationId,
    };

    try {
      trackTakeTicket(source);

      const conversationObj = await makeCloudFunctionRequest('assignConversationToSpecialist', params);
      if (buttonId) {
        // reset button active state - disabled on click to prevent double clicks
        dispatch(setBtnActivity(buttonId, true));
      }

      dispatch(moveTicketToAssignedForSpecialist(conversationObj, simplrExpertObjectId, tabName));
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    }
  };
};

// used to take tickets from other Simplr agents
export const reassignConversationToSpecialist = (conversationId, tabName, buttonId, source = '') => {
  // eslint-disable-next-line
  return async function assignConversationToSpecialist(dispatch) {
    const simplrExpertObjectId = AuthService.current().simplrExpert.id;
    let params = {
      simplrExpertObjectId,
      conversationObjectId: conversationId,
    };

    try {
      trackTakeTicket(source);

      const conversationObj = await makeCloudFunctionRequest('reassignConversationToSpecialist', params);
      if (buttonId) {
        // reset button active state - disabled on click to prevent double clicks
        dispatch(setBtnActivity(buttonId, true));
      }

      dispatch(moveTicketToAssignedForSpecialist(conversationObj, simplrExpertObjectId, tabName));
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    }
  };
};

export const forwardConversationToExperts = (conversationObjId, expert, buttonId, queueName) => {
  return (dispatch, getState) => {
    const { platformDomain, platformTicketId } = extractTicketFromStateById(getState(), conversationObjId);

    let params = {
      simplrExpertObjectId: AuthService.current().simplrExpert.id,
      conversationObjectId: conversationObjId,
      platformDomain,
      platformTicketId,
    };

    if (expert && expert.id) {
      params.forwardToExpertObjectId = expert.id;
    }

    return makeCloudFunctionRequest('forwardConversationToExperts', params)
      .then((response) => {
        // reset button active state - disabled on click to prevent double clicks
        dispatch(setBtnActivity(buttonId, true));

        let conversationObjectId = response.conversationId;
        let queue = getState().tickets[`${queueName}Tickets`];
        let optionalCondition = true;
        if (queueName === ALL) {
          let selectedTicketStatus = getState().selectedTicketStatus;
          if (selectedTicketStatus === TICKET_STATUS_FILTER.all) {
            optionalCondition = false;
          }
        }

        let selectedInboxTab = getState().ui.messages.selectedInboxTab;
        if (queue[conversationObjectId] !== undefined && selectedInboxTab === queueName && optionalCondition) {
          dispatch(removeTicketFromQueue(conversationObjectId, queueName));

          /*
           * selectTicket thunk selects the first ticket in the queue
           * if the queue is not empty and no conversationObj.id is provided
           */
          dispatch(selectTicket('', queueName));
        }
      })
      .catch((error) => {
        handleError({ error, showPopup: true });
      });
  };
};

export const hideConversation = (conversationId) => async (dispatch, getState) => {
  let currentFilter = getState().selectedTicketStatus;
  let selectedTab = getState().ui.messages.selectedInboxTab;
  let expertId = getExpertId();
  let accountType = extractSelectedTicketBusinessDetailsFromState(getState()).accountType;

  let params = {
    conversationId,
    simplrExpertObjectId: expertId,
  };

  try {
    let result;
    if (accountType === 'TEST_STORE') {
      result = await makeCloudFunctionRequest('hideTestConversation', params);
    } else {
      result = await makeCloudFunctionRequest('hideConversation', params);
    }

    const ticket = await getConversationById(result.conversationId);

    // if currentFilter is 'ALL', only update its status. Do not remove
    if (currentFilter === TICKET_STATUS_FILTER.all && selectedTab === ALL) {
      dispatch(updateTicket(ticket, selectedTab, true, expertId));
    } else {
      let queue;
      if (selectedTab === ALL) {
        queue = getState().tickets.allTickets;
      } else if (selectedTab === ASSIGNED) {
        queue = getState().tickets.assignedTickets;
      } else {
        queue = getState().tickets.nominatedTickets;
      }

      if (queue[conversationId] !== undefined) {
        dispatch(removeTicketFromQueue(conversationId, selectedTab));
        /*
         * selectTicket thunk selects the first ticket in the queue
         * if the queue is not empty and no conversationObj.id is provided
         */

        let selectedTicketId = getState().selectedTicketId || '';
        await dispatch(selectTicket(selectedTicketId, selectedTab));
      }

      if (getState().tickets.nominatedTickets[conversationId] !== undefined && currentFilter === 'escalated') {
        dispatch(removeTicketFromQueue(conversationId, NOMINATED));
      }
    }
  } catch (e) {
    showError(e);
    handleError({ error: e });
  }
};

export const unhideConversation = (conversationId, buttonId) => async (dispatch, getState) => {
  const currentFilter = getState().selectedTicketStats;
  const selectedTab = getState().ui.messages.selectedInboxTab;
  const expertId = getExpertId();

  const params = {
    conversationId,
    simplrExpertObjectId: expertId,
  };

  try {
    const result = await makeCloudFunctionRequest('unhideConversation', params);
    dispatch(setBtnActivity(buttonId, true));

    const ticket = await getConversationById(result.conversationId);

    // if currentFilter is 'ALL', only update its status. Do not remove
    if (currentFilter === TICKET_STATUS_FILTER.all && selectedTab === ALL) {
      dispatch(updateTicket(ticket, selectedTab, true, expertId));
    }

    // if in 'hidden' filter, remove from allTicket queue (gets put back on filter switch)
    if (currentFilter === 'hidden' && selectedTab === ALL) {
      const queue = getState().tickets.allTickets;
      if (queue[conversationId] !== undefined) {
        dispatch(removeTicketFromQueue(conversationId, selectedTab));
        /*
         * selectTicket thunk selects the first ticket in the queue
         * if the queue is not empty and no conversationObj.id is provided
         */
        const selectedTicketId = getState().selectedTicketId || '';
        await dispatch(selectTicket(selectedTicketId, selectedTab));
      }
    }
  } catch (e) {
    showError(e);
    handleError({ error: e });
  }
};

export const addMessageToConversation = (message) => {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedTicketId = extractSelectedTicketIdFromState(state);
    const productRecsExist = !isEmpty(extractCommerceWizardRecommendationData(state));

    const conversationId = message.conversationPtr.objectId ?? message.conversationId;

    if (
      getState().conversation.selectedConversationId === conversationId &&
      !getState().conversation[message.objectId]
    ) {
      let processedMessage = processMessage(message);
      dispatch({
        type: types.ADD_MESSAGE,
        message: processedMessage,
        ticketId: selectedTicketId,
        productRecsExist,
      });
    }
  };
};

export const updateMessage = (messageParseObject) => {
  return async (dispatch) => {
    const message = processMessage(messageParseObject);

    dispatch({
      type: types.UPDATE_MESSAGE,
      message,
    });
  };
};

/**
 * Send a message based off the current conversation
 * @param {string} messageTextAsHtml
 * @param {string|undefined} buttonId
 * @param {string|undefined} conversationId if passed a quick reply message will be sent
 * @param lastSimplrMessageBody
 * @param macroCouponCodeTypeId
 * @param {string[]} [userConfirmedWords=[]] - Strings on this list are bypassed from overridable validation.
 */
export const sendMessage = (
  messageTextAsHtml,
  buttonId = null,
  conversationId = null,
  lastSimplrMessageBody = null,
  macroCouponCodeTypeId = null,
  userConfirmedWords = []
) => {
  return async (dispatch, getState) => {
    buttonId && dispatch(setBtnActivity(buttonId, false));

    const latestMessage = htmlToText(
      lastSimplrMessageBody ? lastSimplrMessageBody : !conversationId && getLastSuccessfulMessageFromSimplr(getState())
    );
    const processedMessage = await createMessage(
      getState(),
      messageTextAsHtml,
      conversationId,
      macroCouponCodeTypeId,
      userConfirmedWords
    );
    if (!processedMessage) {
      buttonId && dispatch(setBtnActivity(buttonId, true));
      showErrorMessage(ErrorStrings.COUND_NOT_CREATE_MESSAGE_TO_SEND);

      return Promise.reject(new Error(ErrorStrings.COUND_NOT_CREATE_MESSAGE_TO_SEND));
    }

    const ticket = conversationId
      ? extractTicketFromStateById(getState(), conversationId)
      : extractSelectedTicketFromState(getState());
    if (!ticket) {
      buttonId && dispatch(setBtnActivity(buttonId, true));
      showErrorMessage(ErrorStrings.COUND_NOT_FIND_TICKET_FOR_MESSAGE, true);

      return Promise.reject(new Error(ErrorStrings.COUND_NOT_FIND_TICKET_FOR_MESSAGE));
    }

    const isFirstReply = hasExpertReplied(getState());
    const currentExpert = AuthService.current().simplrExpert;
    const numberOfUnreadBusinessAlerts = getNumberOfUnreadBusinessAlerts(getState());
    const grammarlyActive = !!document.querySelector('.new-message-component gr_block');
    const messageAsText = htmlToText(messageTextAsHtml);

    if (latestMessage === messageAsText) {
      buttonId && dispatch(setBtnActivity(buttonId, true));
      trackSameResponseUsed(messageTextAsHtml);

      return showErrorPopup(`It looks like you already sent that message.`, 'Oops!');
    }

    dispatch(clearUnSentMessage(processedMessage.conversationObjectId));

    return makeCloudFunctionRequest('sendMessageForExpert', processedMessage)
      .then(async (response) => {
        // Keep experts from getting stuck at the end of trees
        dispatch(clearInProgressTreeAndWizardLocation(ticket.ticketId));
        buttonId && dispatch(setBtnActivity(buttonId, true));
        dispatch(setConversationBlockedWordsFound([]));
        dispatch(setConversationOverridableWordsFound([]));

        try {
          SessionAnalyticsService.event('Message Sent', {
            ticket: ticket.ticketId,
            isFirstReply,
            grammarlyActive,
            hadAutoResponse: false,
            simplrTicketNumber: ticket.simplrTicketNumber,
            numberOfUnreadBusinessAlerts,
          });
        } catch (error) {
          handleError({
            error,
            customMessage: 'Error while processing/sending response recommendation analytics',
          });
        }

        const conversation = await getConversationById(processedMessage.conversationObjectId);
        // update ticket in case live query doesn't work (especially for unread count)
        await dispatch(updateTicket(conversation, ASSIGNED, isSpecialist(getState()), currentExpert.id));
        trackSendButtonClick();

        return Promise.resolve(response);
      })
      .catch((err) => {
        buttonId && dispatch(setBtnActivity(buttonId, true));
        dispatch(setConversationBlockedWordsFound([]));
        dispatch(setConversationOverridableWordsFound([]));

        let editorState = getNewMessagesEditor(getState());
        editorState = DraftHelpers.setContents(messageTextAsHtml, editorState);
        dispatch(saveNewMessageEditorState(processedMessage.conversationObjectId, editorState));
        dispatch(setNewMessageCouponCodeType(processedMessage.conversationObjectId, macroCouponCodeTypeId));

        if (err.response?.code === SimplrErrors.Codes.MESSAGE_VALIDATION_ERROR) {
          const blockedWordsFound = err.response?.details?.blockedWords;
          const overridableWordsFound = err.response?.details?.overridableWords;

          if (blockedWordsFound.length) {
            const blockedWordsFoundString = blockedWordsFound?.toString();

            SessionAnalyticsService.event('Blocked Words Used', {
              ticket: processedMessage.conversationObjectId,
              smbName: processedMessage.smbName,
              blockedWordsUsed: blockedWordsFoundString,
            });

            trackBlacklistTextEvent(processedMessage.conversationObjectId, blockedWordsFoundString);

            return dispatch(setConversationBlockedWordsFound(blockedWordsFound));
          }

          if (overridableWordsFound.length) {
            const overridableWordsFoundString = overridableWordsFound?.toString();

            SessionAnalyticsService.event('Overridable Words Used', {
              ticket: processedMessage.conversationObjectId,
              smbName: processedMessage.smbName,
              overridableWordsUsed: overridableWordsFoundString,
            });

            return dispatch(setConversationOverridableWordsFound(overridableWordsFound));
          }
        }

        handleError({ customMessage: 'Error sending message for expert', error: err });

        if (err.message === 'Case currently locked by another user') {
          showErrorMessage(
            'The message failed to send because another user is currently modifying this ticket.' +
              ' Please try again in 5 minutes or escalate with the comment "Ticket is locked."',
            true
          );
        } else if (err.message && err.message.customerReceivedMessage) {
          // currently set up to get description from zendesk api errors
          const errorMessage =
            getZendeskErrorDescription(err) ||
            'The message was sent to the customer but an error was detected in processing the message.';
          showErrorMessage(errorMessage);
          // we do not want to call loadConversation outside of selectTicket - this is an exception
          dispatch(loadConversation(processedMessage.conversationObjectId, ASSIGNED));
        } else {
          showError(err);
        }

        return Promise.reject(err);
      });
  };
};

const convertMessageToNoteParams = (message) => ({
  simplrAccountObjectId: message.simplrAccountObjectId,
  simplrExpertObjectId: AuthService.current().simplrExpert.id,
  conversationObjectId: message.conversationObjectId,
  subject: 'internal note',
  note: message.messageBody,
  noteType: message.noteType ? message.noteType : HINT,
  noteStatus: 'OPEN',
});

/**
 * Create note for selected ticket
 * @param {string} messageBody
 * @param {string} ticketId
 * @param {string} [noteType]
 * @return {function(*): *}
 */
export const addNoteToSpecificTicket =
  (messageBody, ticketId, noteType = HINT) =>
  async (dispatch, getState) => {
    const selectedTicket = extractTicketFromStateById(getState(), ticketId);
    if (!selectedTicket) {
      logMessage('Error: selectedTicket could not be found');

      return showErrorMessage(ErrorStrings.ADDING_NOTE_TO_TICKET_FAILED, true);
    }
    const message = {
      messageBody,
      simplrAccountObjectId: selectedTicket.simplrAccountPtrId,
      conversationObjectId: selectedTicket.ticketId,
      noteType,
    };

    return await dispatch(addNote(message));
  };

/**
 * Create note for selected ticket
 * @param {string} messageBody
 * @param {string} [noteType]
 * @return {function(*): *}
 */
export const addNoteToSelectedTicket =
  (messageBody, noteType = HINT) =>
  async (dispatch, getState) => {
    const selectedTicket = extractSelectedTicketFromState(getState());
    if (!selectedTicket) {
      return showErrorMessage(ErrorStrings.ADDING_NOTE_TO_TICKET_FAILED, true);
    }
    const message = {
      messageBody,
      simplrAccountObjectId: selectedTicket.simplrAccountPtrId,
      conversationObjectId: selectedTicket.ticketId,
      noteType,
    };

    return await dispatch(addNote(message));
  };

/**
 * Create note for selected ticket
 * @param {object} selectedTicket
 * @param {string} messageBody
 * @param {string} [noteType]
 * @return {function(*): *}
 */
export const addNoteToTicket =
  (messageBody, selectedTicket, noteType = HINT) =>
  async (dispatch) => {
    if (!selectedTicket) {
      return showErrorMessage(ErrorStrings.ADDING_NOTE_TO_TICKET_FAILED, true);
    }
    const message = {
      messageBody,
      simplrAccountObjectId: selectedTicket.simplrAccountPtrId,
      conversationObjectId: selectedTicket.ticketId,
      noteType,
    };

    return await dispatch(addNote(message));
  };

/**
 * Create note for conversation
 * @param {object} message
 * @param {string} message.simplrAccountObjectId
 * @param {string} message.conversationObjectId
 * @param {string} message.messageBody
 * @param {string} [message.noteType]
 * @param {string} [buttonId]
 * @return {function(*): *}
 */
export const addNote =
  (message, buttonId = null) =>
  async (dispatch) => {
    const processedMessage = convertMessageToNoteParams(message);
    let note = {};
    try {
      note = await makeCloudFunctionRequest('addNote', processedMessage);
    } catch (error) {
      handleError({ customMessage: 'Error adding note', error });
      showError(error);
    }

    /**
     * Side effects
     */
    if (buttonId) {
      dispatch(setBtnActivity(buttonId, true));
    }

    // returns note that was added
    return note.addedNote;
  };

export const markAsResolved = (ticketId) => {
  return async (dispatch, getState) => {
    const { platformDomain, platformTicketId } = extractTicketFromStateById(getState(), ticketId);
    let params = {
      simplrExpertObjectId: AuthService.current().simplrExpert.id,
      conversationObjectId: ticketId,
      platformDomain,
      platformTicketId,
    };
    dispatch(setTicketRemovalInProcess(true, ticketId));

    try {
      await makeCloudFunctionRequest('closeConversation', params);
    } catch (e) {
      handleError({ error: e });
      dispatch(setTicketRemovalInProcess(false, ticketId));
      const errorMessage = getZendeskErrorDescription(e);
      if (errorMessage) {
        showErrorMessage(errorMessage, true);
      } else {
        showError(e);
      }

      return;
    }

    dispatch(clearUnSentMessage(ticketId));
    let queue = getState().tickets.assignedTickets;
    if (queue[ticketId] !== undefined) {
      dispatch(removeTicketFromQueue(ticketId, ASSIGNED));
      /*
       * selectTicket thunk selects the first ticket in the queue
       * if the queue is not empty and no conversationObj.id is provided
       */
      let selectedTicketId = getState().selectedTicketId || '';
      await dispatch(selectTicket(selectedTicketId, ASSIGNED));
    }
    dispatch(setTicketRemovalInProcess(false, ticketId));
  };
};

// return if it dispatches the remove action
const removeEscalatedTicketFromQueueIfNeeded = (queueName, ticketId) => {
  return (dispatch, getState) => {
    const selectedTicketStatus = getState().selectedTicketStatus;
    const queue = getSelectedQueue(getState());

    // If escalation occurs in the ALL tab with the ALL filter selected we do NOT want to remove the ticket from the queue and select the next ticket in the queue
    const ticketNotInAllQueue = queue[ticketId] !== undefined && queueName !== ALL;
    const ticketInAllQueueWithoutAllFilter =
      queue[ticketId] !== undefined && queueName === ALL && selectedTicketStatus !== TICKET_STATUS_FILTER.all;

    if (ticketNotInAllQueue || ticketInAllQueueWithoutAllFilter) {
      dispatch(removeTicketFromQueue(ticketId, queueName));

      return true;
    }

    return false;
  };
};

export const updateEscalatedTicketInQueues = (escalatedTicketId) => {
  return async (dispatch, getState) => {
    dispatch(clearUnSentMessage(escalatedTicketId));

    const selectedTicketId = extractSelectedTicketIdFromState(getState());
    // might be in all queue and assigned
    const queueNames = getQueueNamesForTicket(getState(), escalatedTicketId);
    const ticketSelectionPromises = queueNames.map(async (queueName) => {
      const didRemove = dispatch(removeEscalatedTicketFromQueueIfNeeded(queueName, escalatedTicketId));

      if (didRemove && escalatedTicketId === selectedTicketId) {
        const newSelectedInboxTab = extractSelectedInboxTab(getState());
        // eslint-disable-next-line
        const selectedTicketId = chooseDefaultTicket(newSelectedInboxTab, getState());

        if (selectedTicketId) {
          await dispatch(selectTicket(selectedTicketId, newSelectedInboxTab));
        }
      }
    });

    await Promise.all(ticketSelectionPromises);
  };
};

export const escalateCurrentTicket = (
  escalateMessage,
  {
    reasonSelection,
    customMessage,
    customerEmail,
    asPerBusinessRule,
    escalateToBusinessAsExpert,
    targetedDepartment = '',
  } = {},
  ticket = null
) => {
  return async (dispatch, getState) => {
    const state = getState();
    const outboundEscalationObject = getOutboundEscalationObject(state);

    if (!canEscalateCurrentTicket(state, ticket)) {
      showErrorMessage(ErrorStrings.CANNOT_ESCALATE_TICKET, true);

      return Promise.reject(new Error(ErrorStrings.CANNOT_ESCALATE_TICKET));
    }

    ticket = ticket || extractSelectedTicketFromState(state);
    if (!ticket) {
      handleError({ customMessage: 'Error: selectedTicket could not be found' });
      showErrorMessage(ErrorStrings.ESCALATION_ATTEMPT_FAILED);

      return Promise.reject(new Error(ErrorStrings.ESCALATION_ATTEMPT_FAILED));
    }

    let params = {
      simplrExpertObjectId: AuthService.current().simplrExpert.id,
      conversationObjectId: ticket.ticketId,
      escalateMessage,
      reasonSelection,
      escalateToBusinessAsExpert,
      customMessage,
      customerEmail,
      asPerBusinessRule,
      outboundEscalationObject,
      targetedDepartment,
      platformDomain: ticket.platformDomain,
      platformTicketId: ticket.platformTicketId,
      isManualEscalation: true,
    };

    if (!isLiveChat(ticket.communicationType)) {
      delete params[outboundEscalationObject];
    }

    try {
      dispatch(setTicketRemovalInProcess(true, ticket.ticketId));
      trackEscalatedTicket(escalateMessage, ticket);
      await escalateConversation(params);
      await dispatch(updateEscalatedTicketInQueues(ticket.ticketId));
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    } finally {
      dispatch(setTicketRemovalInProcess(false, ticket.ticketId));
    }

    // TODO move to back end
    const {
      conversation,
      user: { isAdmin },
    } = state;
    if (isAdmin && !isEscalatableProvider(ticket)) {
      const emailData = formatEscalateEmail({
        to: ticket.escalateToSmbEmail,
        smbName: ticket.smbName,
        ticketNumber: ticket.simplrTicketNumber,
        customerContact: ticket.fromChannel,
        escalationReason: escalateMessage,
        conversation: conversation,
      });
      dispatch(sendGenericEmail(emailData));
    }
  };
};

export const tagTicketAsOutOfScope = (ticket, successFeedback) => {
  return async (dispatch, getState) => {
    try {
      const selectedTab = getState().ui.messages.selectedInboxTab;
      await autoEscalateConversation(ticket.ticketId, ticket.platformDomain, ticket.platformTicketId);
      dispatch(removeTicketFromQueue(ticket.ticketId, selectedTab));

      const selectedTicketId = chooseDefaultTicket(selectedTab, getState());

      if (selectedTicketId) {
        await dispatch(selectTicket(selectedTicketId, selectedTab));
      }
      if (isFunction(successFeedback)) {
        successFeedback();
      }
    } catch (e) {
      if (e instanceof CloudFunctionRequestError) {
        showError(e);
      } else {
        throw e;
      }
    }
  };
};

const loadEmptyHistoricalConversation = () => ({
  type: types.LOAD_CONVERSATION,
  conversation: { allMessages: [], selectedConversationId: '' },
});

export const loadHistoricalConversation = (requestedConversationId, tabName, isExternalHistoricalTicket) => {
  return async (dispatch, getState) => {
    dispatch(setLoading(true, 'messages'));
    dispatch(setTagOnForwardStatus(false));
    dispatch(clearBusinessAlerts());

    if (!requestedConversationId) {
      dispatch(loadEmptyHistoricalConversation());
      dispatch(setLoading(false, ''));

      return;
    }

    const state = getState();

    const currentlyOpenedConversationId = state.conversation.selectedConversationId;

    // Checking if the requested conversation is already loaded
    if (requestedConversationId === currentlyOpenedConversationId) {
      dispatch(setLoading(false, ''));

      return;
    }

    try {
      const conversation = state.tickets.historicalTickets[requestedConversationId];
      const historicalConversation = await makeCloudFunctionRequest('getHistoricalConversation', {
        conversation,
        isExternal: !!isExternalHistoricalTicket,
      });

      if (!historicalConversation) {
        dispatch(loadEmptyHistoricalConversation());
        dispatch(setLoading(false, ''));

        return;
      }

      const selectedTicketId = extractSelectedTicketIdFromState(getState());
      if (tabName === getState().ui.messages.selectedInboxTab && selectedTicketId === requestedConversationId) {
        dispatch({
          type: types.LOAD_CONVERSATION,
          conversation: historicalConversation,
        });
      }

      dispatch(setLoading(false, ''));
    } catch (err) {
      showError(err);
    }
  };
};

const loadActionsInfo = (ticketId) => {
  return async (dispatch, getState) => {
    const state = getState();
    // TODO replace with getSimplrAccountIdOfSelectedTicket?
    const selectedTicket = extractTicketFromStateById(state, ticketId);
    const brand = getBrandOfSelectedTicket(state);
    const promises = [
      dispatch(loadECommerceIntegrations(selectedTicket.simplrAccountPtrId)), // classic integration info
      dispatch(
        loadDynamicIntegrations({
          simplrAccountId: selectedTicket.simplrAccountPtrId,
          brand,
        })
      ), // dynamic integration info (new method)
    ];
    await Promise.all(promises);
  };
};

export const setTaggingActive = (value) => ({
  type: types.SET_TAGGING_ACTIVE,
  value,
});

export const showNudgePopup = (value) => ({
  type: types.TOGGLE_NUDGE_POPUP,
  payload: value,
});

export const setDebugModeEnabled = (isToggledOn) => {
  return async (dispatch, getState) => {
    const state = getState();
    const firstMessage = extractFirstMessage(state);

    dispatch({
      type: types.SET_DEBUG_MODE,
      value: isToggledOn,
    });

    /**
     * Only the enabled debug mode query will actually fetch the flow debug state field.
     * Once it has been fetched once, prevent further fetching of current conversation
     * even if the user spams the button.
     */
    if (isToggledOn && firstMessage.flowDebugState === undefined) {
      const selectedTicketId = extractSelectedTicketIdFromState(state);
      const ticket = extractSelectedTicketFromState(state);
      const simplrAccountId = get(ticket, 'simplrAccountPtrId');
      const conversation = await getConversation(selectedTicketId, isToggledOn, simplrAccountId);

      dispatch({
        type: types.LOAD_CONVERSATION,
        conversation,
      });
    }
  };
};

// use hideTools instead - this action alone does not persist wizard state
export const setShowTools = (showTools) => {
  return async (dispatch, getState) => {
    const state = getState();

    dispatch({
      type: types.SHOW_TOOLS,
      value: showTools,
    });

    if (showTools) {
      dispatch(setToolsLoading(true));
      const selectedTicket = extractSelectedTicketFromState(state);
      if (!selectedTicket) {
        return showErrorMessage(ErrorStrings.SELECTED_TICKET_NOT_FOUND);
      }
      dispatch(setCenterPaneExtraInfoPanelActive(false, null));
      await dispatch(loadActionsInfo(selectedTicket.ticketId));
      dispatch(setToolsLoading(false));
    }
  };
};

export const setToolsLoading = (value) => ({
  type: types.TOOLS_LOADING,
  value,
});

export const updateLongTransactionLoadingStatus = (ticketId, value) => {
  return {
    type: types.UPDATE_LONG_TRANSACTION_LOADING_STATUS,
    value,
    ticketId,
  };
};

export const updateAvailabilitySwitchLockExp = (value) => {
  return {
    type: types.UPDATE_AVAILABILITY_SWITCH_LOCK_EXP,
    value,
  };
};

export const clearHistoricalTicket = () => ({
  type: types.CLEAR_HISTORICAL_TICKET,
});

export const setMockPageState = (state) => ({
  type: types.SET_MOCK_PAGE_STATE,
  state,
});

export const sendGenericEmail = (emailData) => {
  return () => {
    return makeCloudFunctionRequest('sendGenericEmail', emailData).catch((error) => {
      // This promise is catching an error even though the email sends successfully
      // disabling showError popup reason is
      handleError({ error, customMessage: 'Error sending generic email' });
    });
  };
};

export const refreshTickets = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const isAdmin = isSpecialist(getState());
    const actions = isAdmin ? await loadTicketsAdmin(state) : await loadTicketsNonAdmin(state);
    actions.forEach(dispatch);
  };
};

export const startOnloadLiveQuery = () => {
  return {
    type: types.START_ONLOAD_LIVE_QUERY,
    liveQueryStarted: true,
  };
};

export const loadLongTransaction = () => {
  return {
    type: types.LOAD_LONG_TRANSACTION,
    liveQueryStarted: true,
  };
};

export const updateExpertToggleLockConfirmation = (value) => {
  return {
    type: types.UPDATE_EXPERT_TOGGLE_LOCK_CONFIRMATION,
    value,
  };
};

export const rejectConversationFromExpert = (conversationId, buttonId) => {
  return (dispatch, getState) => {
    const params = {
      expertId: AuthService.current().simplrExpert.id,
      conversationId: conversationId,
    };

    return makeCloudFunctionRequest('rejectConversationFromExpert', params)
      .then(() => {
        if (buttonId) {
          // reset button active state - disabled on click to prevent double clicks
          dispatch(setBtnActivity(buttonId, true));
        }

        let queue = getState().tickets.nominatedTickets;
        let selectedInboxTab = getState().ui.messages.selectedInboxTab;
        if (queue[conversationId] !== undefined && selectedInboxTab === NOMINATED) {
          dispatch(removeTicketFromQueue(conversationId, NOMINATED));

          /*
           * selectTicket thunk selects the first ticket in the queue
           * if the queue is not empty and no ticketId is provided
           */
          dispatch(selectTicket('', NOMINATED));
        }
      })
      .catch((err) => {
        handleError({ customMessage: 'Error rejecting conversation', error: err });
        showError(err);
      });
  };
};

export const resetNumChatActiveAssignedForExpert = (simplrExpertId) => ({
  type: types.RESET_NUM_CHAT_ACTIVE_ASSIGNED_FOR_EXPERT,
  simplrExpertId,
});

export const updateSimplrCategories = (simplrCategories, simplrAccountId) => ({
  type: types.SET_BUSINESS_SIMPLR_CATEGORIES,
  simplrCategories,
  simplrAccountId,
});

export const setPartnerInfo = (partnerInfo, simplrAccountId) => ({
  type: types.SET_PARTNER_INFO,
  partnerInfo,
  simplrAccountId,
});

export const setTagsConfig = (tagsConfig, simplrAccountId) => ({
  type: types.SET_TAGS_CONFIG,
  tagsConfig,
  simplrAccountId,
});

export const setDecisionTree = (decisionTree, simplrAccountId) => ({
  type: types.SET_DECISION_TREE,
  decisionTree,
  simplrAccountId,
});

export const setFlowDebugStateFavoriteFieldsList = (flowDebugStateFavoriteFieldsList, simplrAccountId) => ({
  type: types.SET_FLOW_DEBUG_STATE_FAVORITE_FIELDS_LIST,
  flowDebugStateFavoriteFieldsList,
  simplrAccountId,
});

export const setDecisionTreeDrafts = (decisionTreeDrafts, simplrAccountId) => ({
  type: types.SET_DECISION_TREE_DRAFTS,
  decisionTreeDrafts,
  simplrAccountId,
});

export const setUseMenuOperator = (status, simplrAccountId) => ({
  type: types.SET_USE_MENU_OPERATOR,
  status,
  simplrAccountId,
});

export const updateSubBusinessVerticals = (subBusinessVerticals, simplrAccountId) => ({
  type: types.SET_SUBBUSINESS_VERTICALS,
  subBusinessVerticals,
  simplrAccountId,
});

export const setTicketRemovalInProcess = (value, ticketId) => ({
  type: types.SET_TICKET_REMOVAL_IN_PROCESS,
  value,
  ticketId,
});

export const changeLoginStatus = (isLoggedIn) => {
  return {
    type: types.CHANGE_LOGIN_STATUS,
    isLoggedIn: isLoggedIn,
  };
};

export const setMFAInformation = (mfaToken, phoneNumberLastDigits) => {
  return {
    type: types.SET_MFA_INFORMATION,
    mfaToken,
    phoneNumberLastDigits,
  };
};

export const updateUser = (user) => {
  return {
    type: types.UPDATE_USER,
    user: user,
  };
};

/**
 * @deprecated use setLoadingUnique instead, this one works only
 * for one components at a time, and will lose loading state when loading in parallel.
 * Also you can use isLoadingUnique from extractFromState to help get the loading state
 */
export const setLoading = (isLoading, component = '') => {
  return {
    type: types.SET_LOADING,
    isLoading,
    component,
  };
};

// this new setLoading will make sure that loading components don't overlap and cancel each other's loading state
export const setLoadingUnique = (isLoading, component = '') => ({
  type: types.SET_LOADING_UNIQUE,
  isLoading,
  component,
});

export const setLoadingMessage = (message) => {
  return {
    type: types.SET_LOADING_MESSAGE,
    message,
  };
};

export const setBtnActivity = (buttonId, isActive) => {
  return {
    type: types.SET_BTN_ACTIVITY,
    buttonId,
    isActive,
  };
};

export const setTagOnForwardStatus = (value) => ({
  type: types.SET_TAG_ON_FORWARD_STATUS,
  value,
});

export const saveNewMessageEditorState =
  (conversationObjId, newEditorState, decisionTreeCouponCode, chatProductRecTrigger = false) =>
  (dispatch, getState) => {
    const state = getState();
    const ticketId = extractSelectedTicketIdFromState(state);
    const selectedTicket = extractSelectedTicketFromState(state);
    const isChatTicket = isLiveChat(selectedTicket.communicationType);

    // local state - triggered even if no product is selected
    const recommendationData = get(state, `mlData.recommendedProducts[${ticketId}]`);
    // persist data from server - only triggered if product is recommended
    const savedPreviouslyRecommendedProducts = extractPreviouslyRecommendedProducts(state);
    const previouslyTriggeredRecommendations =
      !recommendationData ||
      recommendationData.previouslyTriggeredRecommendations ||
      savedPreviouslyRecommendedProducts;

    const crossSellProducts = extractCommerceWizardCrossSellProducts(state);
    const upSellProducts = extractCommerceWizardUpsSellProducts(state);
    const globalProducts = extractCommerceWizardGlobalProduct(state);
    const toDisplay = get(recommendationData, 'toDisplay', false);
    const productRecommendationsExist =
      !isEmpty(crossSellProducts) || !isEmpty(upSellProducts) || !isEmpty(globalProducts);
    let editorState = newEditorState;
    if (editorState) {
      const newText = editorState.getCurrentContent().getPlainText();
      const hasRecommendationTrigger = isChatTicket
        ? chatProductRecTrigger
        : newText.includes(triggers.RECOMMEND_A_PRODUCT_TRIGGER);
      if (
        hasRecommendationTrigger &&
        recommendationData &&
        !previouslyTriggeredRecommendations &&
        productRecommendationsExist &&
        toDisplay &&
        !isChatTicket
      ) {
        commerceRecosDisplayed();
        dispatch(clearCommerceWizardState());
        dispatch(setCenterPaneExtraInfoPanelActive(true, null, null, infoPanelTypes.COMMERCE));
        dispatch(setRecommendationAsPreviouslyTriggered(ticketId));
      } else if (hasRecommendationTrigger && !isChatTicket) {
        editorState = DraftHelpers.replaceRegularExpression(
          '',
          editorState,
          triggers.RECOMMEND_A_PRODUCT_TRIGGER,
          true
        );
      }
    }

    dispatch({
      type: types.SAVE_NEW_MESSAGE_EDITOR_STATE,
      conversationObjId: conversationObjId,
      editorState: editorState,
      decisionTreeCouponCode,
    });
  };

export const copyMacroTextAction =
  (macroText = null) =>
  async (dispatch, getState) => {
    const state = getState();
    const { ticketType, communicationType } = extractSelectedTicketFromState(state);
    const editorState = getNewMessagesEditor(state);
    const templatedCopyText = macroText ?? getTemplatedCopyTextFromCurrentDecisionNode(state);
    const selectedConversationId = extractNonHistoricalTicketIdFromState(state);
    const selectedDiscountPeriodCouponCode = getSelectedDiscountPeriodCouponCode(state);
    const macro = messageToClientFormat(templatedCopyText, ticketType, communicationType);
    const newEditorState = DraftHelpers.appendHtmlToContents(macro, editorState);

    trackDecisionTreeTextCopied(SEARCH_ACTION_SOURCES.COPY_BUTTON);

    dispatch(saveNewMessageEditorState(selectedConversationId, newEditorState, selectedDiscountPeriodCouponCode));
    dispatch(onPasteText(templatedCopyText));
  };

export const setNewMessageCouponCodeType = (conversationObjId, macroCouponCodeTypeId) => {
  return {
    type: types.SET_NEW_MESSAGE_COUPON_CODE_TYPE,
    conversationObjId: conversationObjId,
    macroCouponCodeTypeId,
  };
};

export const setEscRecoSeen = (conversationObj) => {
  return {
    type: types.SET_ESC_RECO_SEEN,
    conversationObjId: conversationObj.ticketId,
  };
};

export const setActiveEscRecoPopupId = (id) => {
  return {
    type: types.SET_ACTIVE_ESC_RECO_POPUP_ID,
    payload: id,
  };
};

export const clearUnSentMessage = (conversationObjId) => {
  return {
    type: types.CLEAR_UNSENT_MESSAGE,
    conversationObjId: conversationObjId,
  };
};

export const setBlacklist = (simplrExpertId, blacklist) => ({
  type: types.SET_BLACKLIST,
  simplrExpertId,
  blacklist,
});

export const setChatBlacklist = (simplrExpertId, chatBlacklist) => ({
  type: types.SET_CHAT_BLACKLIST,
  simplrExpertId,
  chatBlacklist,
});

export const setMinimumExpertRank = (simplrBusinessId, rank) => ({
  type: types.SET_MINIMUM_EXPERT_RANK,
  simplrBusinessId,
  rank,
});

export const onPasteText = (pastedText) => (dispatch, getState) => {
  const ticketId = extractSelectedTicketIdFromState(getState());
  dispatch({
    type: types.ON_PASTE_TEXT,
    ticketId,
    pastedText,
  });
};

export const toggleChatAlertDisabled = (value) => ({
  type: types.TOGGLE_CHAT_ALERT_DISABLED,
  value,
});

export const toggleNonChatAlertDisabled = (value) => ({
  type: types.TOGGLE_NON_CHAT_ALERT_DISABLED,
  value,
});

export const toggleChatAlertOnAndOff = () => (dispatch) => {
  dispatch(toggleChatAlertDisabled(true));
  setTimeout(() => dispatch(toggleChatAlertDisabled(false)), 10000);
};

export const toggleNonChatAlertOnAndOff = () => (dispatch) => {
  dispatch(toggleNonChatAlertDisabled(true));
  setTimeout(() => dispatch(toggleNonChatAlertDisabled(false)), 10000);
};

export const loadPartnerTagsForSelectedTicketOfTab = () => async (dispatch, getState) => {
  const ticket = extractSelectedTicketFromState(getState());

  try {
    if (!ticket || Object.keys(ticket).length === 0) {
      throw Error('Selected ticket is undefined');
    }

    if (ticket.isTestTicket) {
      return true;
    }

    await dispatch(getPartnerTags(ticket));

    // return true that the call suceeds
    return true;
  } catch (err) {
    handleError({ error: err });
    showExpressError(err, 'Unable to retrieve partner tags. Please try again later.');

    trackErrorFetchingSmbCategories({
      ticket,
      error: err,
    });

    // return false that it failed
    return false;
  }
};

export const decisionTreeCaseNotHandled = (passedEsclationMessage, errorName) => async (dispatch, getState) => {
  const state = getState();
  const isAdmin = isSpecialist(state);
  const { escalationMessage, action, targetedDepartment = '' } = getDecisionNodeAtPath(state);

  // clone so we don't mutate
  const popupConfig = cloneDeep(CANNOT_HANDLE_POPUP_CONFIG);
  if (escalationMessage) {
    popupConfig.content.subText = [escalationMessage];
  }

  // node message takes priority
  const messageForAnalytics = escalationMessage || passedEsclationMessage;
  // "Escalation Reason: " prefix needed by operations on ticket note
  const analyticsError = addPrefixToEsclationMessage(messageForAnalytics);
  trackDecisionTreeCaseNotHandled(analyticsError);

  // for admin, show popup and kick them out of decision tree
  if (isAdmin || errorName === 'UnhandledDecisionTreeCaseMultipleOrders') {
    // show popup, no action
    popupConfig.content.main = 'Not handling this ticket through the decision tree';
    if (passedEsclationMessage && passedEsclationMessage !== escalationMessage) {
      // prefix unneeded here
      popupConfig.content.subText.push(passedEsclationMessage);
    }

    oneBtnPopupCreator(popupConfig);
    // reset path
    dispatch(clearMacroWizardPath());
  } else {
    // escalate ticket to partner
    try {
      // if decision node action is ESCALATE_TO_PARTNER or ESCALATE_TO_PARTNER_CUSTOM_DEPARTMENT
      // set to true, otherwise set it to false since it should escalate to specialists
      const escalationNodesActions = [
        DECISION_NODE_ACTIONS.ESCALATE_TO_PARTNER,
        DECISION_NODE_ACTIONS.ESCALATE_TO_PARTNER_CUSTOM_DEPARTMENT,
      ];

      const escalateToBusinessAsExpert = escalationNodesActions.includes(action);
      dispatch(clearMacroWizardPath());
      await dispatch(
        escalateCurrentTicket(analyticsError, {
          escalateToBusinessAsExpert,
          targetedDepartment,
          asPerBusinessRule: true,
        })
      );
      oneBtnPopupCreator(popupConfig);
    } catch (error) {
      handleError({ error });
    }
  }
};

export const setEscalationMessage = (escalationMessage) => ({
  type: types.SET_ESCALATION_MESSAGE,
  escalationMessage,
});

export const setChatMacroRecommendationsEnabled = (chatMacroRecommendationsEnabled) => ({
  type: types.SET_CHAT_MACRO_RECOMMENDATIONS_ENABLED,
  chatMacroRecommendationsEnabled,
});

export const setOutboundEscalationObject = (outboundEscalationObject) => ({
  type: types.SET_OUTBOUND_ESCALATION_MESSAGE,
  outboundEscalationObject,
});

export const setCenterPaneExtraInfoPanelActive = (value, order, subscription, listType) => ({
  type: types.SET_CENTER_PANE_EXTRA_INFO_PANEL_ACTIVE,
  value,
  order,
  subscription,
  listType,
});

export const temporaryUnlockSensitiveData = (dataType, component, customerId) => (dispatch) => {
  dispatch({ type: types.UNLOCK_SENSITIVE_DATA, dataType, component, customerId });

  setTimeout(() => {
    dispatch({ type: types.LOCK_SENSITIVE_DATA, dataType, component, customerId });
  }, 9e5);
};

export const loadDefaultParseConfigs = () => async (dispatch) => {
  try {
    const configs = await getGlobalParseConfigsList([
      'selectedPartners',
      'monitoredTicketIdFirstChar',
      'partnerProtectorRatingToolConfig',
      'specialistRatingToolConfig',
      'copiedTextSourceOptions',
      'enableChatQuickReplies',
      'debounceAmountForGetPredictionText',
    ]);

    dispatch({
      type: types.SET_PARSE_CONFIGS,
      configs,
    });
  } catch (error) {
    showExpressError(error, 'Error loading global configs. Please try to refresh the page.');
  }
};

export const setLoadingForMacroWizard = (value) => ({
  type: types.SET_LOADING_FOR_MACRO_WIZARD,
  value,
});

export const setPreviousAppVersionFile = (versionFile) => ({
  type: types.SET_PREVIOUS_APP_VERSION_FILE,
  versionFile,
});

export const setShowAppVersionRefreshNotification = (value) => ({
  type: types.SET_SHOW_APP_VERSION_REFRESH_NOTIFICATION,
  value,
});

export const setPartnerTags = (partnerTagsArray) => ({
  type: types.SET_PARTNER_TAGS,
  partnerTagsArray,
});

export const handleReviewTicketSelect = (selectedTicketId) => async (dispatch, getState) => {
  const state = getState();
  const selectedInboxTab = extractSelectedInboxTab(state);
  dispatch(clearHistoricalTicket());
  dispatch(closePreviousConversationsSection());
  dispatch(
    hydrateHistoricalTickets({
      historicalTicketsTotalCount: 0,
      allTickets: [],
    })
  );
  dispatch({
    type: types.SELECTED_TOOL,
    name: 'ACTIONS',
  });
  dispatch({
    type: types.SELECT_REVIEW_TICKET,
    selectedTicketId,
  });

  const ticket = extractTicketFromStateById(state, selectedTicketId);
  const simplrAccountId = get(ticket, 'simplrAccountPtrId');

  const actions = [dispatch(loadConversation(selectedTicketId, selectedInboxTab))];

  if (simplrAccountId) {
    actions.push(dispatch(loadBusinessDetailsItem(simplrAccountId)));
  }

  await Promise.all(actions);
};

export const handleValidationTicketSelect = (selectedTicketId) => async (dispatch, getState) => {
  const state = getState();
  const selectedInboxTab = extractSelectedInboxTab(state);
  dispatch(clearHistoricalTicket());
  dispatch(closePreviousConversationsSection());
  dispatch(
    hydrateHistoricalTickets({
      historicalTicketsTotalCount: 0,
      allTickets: [],
    })
  );
  dispatch({
    type: types.SELECTED_TOOL,
    name: 'ACTIONS',
  });
  dispatch({
    type: types.SELECT_VALIDATION_TICKET,
    selectedTicketId,
  });

  const ticket = extractTicketFromStateById(state, selectedTicketId);
  const simplrAccountId = get(ticket, 'simplrAccountPtrId');

  const actions = [dispatch(loadConversation(selectedTicketId, selectedInboxTab))];

  if (simplrAccountId) {
    actions.push(dispatch(loadBusinessDetailsItem(simplrAccountId)));
  }

  await Promise.all(actions);
};

export const updateReviewTicketInQueue = (ticket) => ({
  type: types.UPDATE_REVIEW_TICKET,
  ticket,
});

export const removeReviewTicketFromQueue = (ticketId) => ({
  type: types.REMOVE_REVIEW_TICKET,
  ticketId,
});

export const removeValidationTicketFromQueue = (ticketId) => ({
  type: types.REMOVE_VALIDATION_TICKET,
  ticketId,
});

export const clearSelectedTicket = () => ({
  type: types.CLEAR_SELECTED_TICKET,
});

export const setClipboardText = (text) => (dispatch, getState) => {
  const selectedTicketId = extractSelectedTicketIdFromState(getState());
  if (selectedTicketId) {
    dispatch({
      type: types.SET_CLIPBOARD_TEXT,
      text,
      selectedTicketId,
    });
  }
};

export const setShowSpecialistTools = (value) => ({
  type: types.SET_SHOW_SPECIALIST_TOOLS,
  value,
});

export const setIsExpertDashboardOpened = (value) => ({
  type: types.SET_EXPERT_DASHBOARD_OPENED,
  value,
});

export const setChatAlertActiveId =
  (value, options = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const isAdmin = isSpecialist(state);
    const { forceOverride = false } = options;

    if (!isAdmin) {
      const firstNominatedNotification = extractFirstNominatedNotification(state);
      const nominatedChatAlertActive = extractNominatedNotificationActive(state);

      let activeId = value;
      if (!forceOverride) {
        // used to force new notifs to close on timeout
        if (!nominatedChatAlertActive) {
          if (!!firstNominatedNotification && !activeId) {
            // if you close a chat notif. and new notif is available, auto open
            activeId = firstNominatedNotification.ticketId;
          }
        } else if (!!nominatedChatAlertActive && !activeId) {
          // if you try to close a new notif without opening another you cannot
          activeId = firstNominatedNotification.ticketId;
        }
      }

      const chatAlertActiveId = getChatAlertActiveId(getState());

      if (activeId !== chatAlertActiveId) {
        dispatch({
          type: types.SET_CHAT_ALERT_ACTIVE_ID,
          activeId,
        });
      }
    }
  };

export const addNoteOnDecisionTreeOpened = (ticketId, simplrTag, tagHistoryBeforeUpdate) => (dispatch, getState) => {
  if (!ticketId || !simplrTag || !Array.isArray(tagHistoryBeforeUpdate)) {
    return;
  }

  const state = getState();
  const userId = get(state, 'user.id', null);

  const expertUsedCurrentTag = tagHistoryBeforeUpdate.some(
    (tag) => tag.simplrTag === simplrTag && tag.expertId === userId
  );

  if (expertUsedCurrentTag) {
    return;
  }

  const decisionTreeOfSelectedTicket = extractDecisionTreeOfSelectedTicket(state);
  const selectedDecisionTree = get(decisionTreeOfSelectedTicket, `${simplrTag}`, null);

  if (!selectedDecisionTree) {
    return;
  }

  const treeTitle = get(selectedDecisionTree, `metadata.title`, null);
  const noteMessage = treeTitle
    ? `Opened the ${treeTitle} decision tree under ${simplrTag} tag`
    : `Opened the decision tree under ${simplrTag} tag`;

  dispatch(addNoteToSpecificTicket(noteMessage, ticketId, SPECIALIST_ONLY_HINT));
};

export const hideTools = () => (dispatch) => {
  dispatch(setInProgressTreeAndWizardLocation());
  dispatch(setTaggingActive(false));
  dispatch(setTagOnForwardStatus(false));
  dispatch(setShowTools(false));
};

/**
 * @param {object} conversation
 */
export const clearQueue = (conversation) => async () => {
  try {
    await clearStuckTickets(conversation?.selectedConversationId);
  } catch (err) {
    showError(err);
  }
};
